import React from "react";
import { learnerCountDisplay } from "../../../utils/learnerCount";
import { Link } from "react-router-dom";
import ModalStore from "../../../stores/ModalStore";
import Terms from "../Terms";
import TermsAndConditions from "../../TermsAndConditions";
import { PaymentType, Priceable } from "../Types";
import { Quotation } from "../../../stores/CoursePreviewStore/Types";
import PlanInfo from "../PlanInfo";

interface Props {
  paymentType: PaymentType;
  activeQuote: Quotation;
  isCourseError: boolean;
  course: string;
  slug: string;
  path: string;
  selectedCourse: string;
  category: string;
  couponCode: string;
  refCode: string;
  monthlySubsequentPayment: string;
  monthlyPlanTotal: string;
  priceable: Priceable;
  quotes: Quotation[];
  loading: boolean;
  loadingSubscription: boolean;
  handleChange: (key: string, value: any) => void;
  refreshQuote: () => void;
  refreshQuoteWithoutCoupon: (e: any) => void;
}

const OrderSummary: React.FC<Props> = ({
  paymentType,
  activeQuote,
  loading,
  priceable,
  couponCode,
  refCode,
  quotes,
  category,
  monthlySubsequentPayment,
  monthlyPlanTotal,
  slug,
  path,
  isCourseError,
  selectedCourse,
  loadingSubscription,
  handleChange,
  refreshQuote,
  refreshQuoteWithoutCoupon,
}) => {
  const [showIncluded, setShowIncluded] = React.useState(false);
  const isGiftCheckout = paymentType === "gift";
  const isGiftCourse = paymentType === "gift" && priceable?.type === "course";
  const isGiftPass = paymentType === "gift" && priceable?.type === "pass";
  const isCourseCheckout = paymentType === "course" || isGiftCourse;
  const isLifetimeCheckout =
    (paymentType === "subscription" && slug === "lifetime") ||
    (isGiftPass && priceable?.is_lifetime);
  const isCommunityCheckout =
    (paymentType === "subscription" && slug === "community") ||
    (isGiftPass && priceable?.is_community);
  const isSubscriptionCheckout =
    paymentType === "subscription" || isGiftPass;
  const disableGiftTabSwitching = (e) => {
    if (isGiftCheckout) e.preventDefault();
  };
  const tabClassName = (isActive) =>
    `${isActive ? "active" : ""} ${
      isGiftCheckout ? "click-disabled" : ""
    }`.trim();

  const isPassCheckout =
    isSubscriptionCheckout && !isCommunityCheckout && !isLifetimeCheckout;

  return (
    <aside className="summary-side" aria-label="Order summary">
      {/* The same three doors as the pricing cards, in the same order. The
          Community Hub is still sold at its own address, but has no tab
          (Chris, 29 Oct 2026); on its checkout no tab is selected. Each one
          opens another checkout, so this is navigation with the current
          checkout marked, not a set of tabs. */}
      <nav className="plan-switcher" id="planSwitcher" aria-label="Choose plan">
        <Link
          to="/pay/course"
          data-plan="single"
          onClick={disableGiftTabSwitching}
          className={tabClassName(isCourseCheckout)}
          aria-current={isCourseCheckout ? "page" : undefined}
          aria-disabled={isGiftCheckout}
          tabIndex={isGiftCheckout ? -1 : undefined}
        >
          Single course
        </Link>

        <Link
          to="/passes/year/pay"
          data-plan="all-access"
          onClick={disableGiftTabSwitching}
          className={tabClassName(isPassCheckout)}
          aria-current={isPassCheckout ? "page" : undefined}
          aria-disabled={isGiftCheckout}
          tabIndex={isGiftCheckout ? -1 : undefined}
        >
          All Courses Pass
        </Link>

        <Link
          to="/passes/lifetime/pay"
          data-plan="lifetime"
          onClick={disableGiftTabSwitching}
          className={tabClassName(isLifetimeCheckout)}
          aria-current={isLifetimeCheckout ? "page" : undefined}
          aria-disabled={isGiftCheckout}
          tabIndex={isGiftCheckout ? -1 : undefined}
        >
          Lifetime access
        </Link>
      </nav>

      {/* Members upgrading from the Pass are invited; anyone else came from
          the pricing cards, and Lifetime is often bought with a training
          budget, so they are pointed at the company invoice instead. */}
      <div
        className="invitation-badge"
        id="invitationBadge"
        style={{
          display: isLifetimeCheckout && !isGiftCheckout ? "flex" : "none",
        }}
      >
        {priceable?.discounted ? (
          <>
            <svg className="star-icon" aria-hidden="true">
              <use href="#icon-star" />
            </svg>
            <div className="text">
              <strong>You've been invited</strong>
              <span>
                Offered only to members who've proven themselves to be
                thoroughly good eggs.
              </span>
            </div>
          </>
        ) : (
          <>
            <svg className="star-icon" aria-hidden="true">
              <use href="#icon-invoice" />
            </svg>
            <div className="text">
              <strong>Buying with a training budget?</strong>
              <span>
                Click "I need a company invoice" to add your company details
                for whoever holds the purse strings.
              </span>
            </div>
          </>
        )}
      </div>

      <PlanInfo
        paymentType={paymentType}
        slug={slug}
        path={path}
        isCourseError={isCourseError}
        priceable={priceable}
        course={selectedCourse}
        category={category}
        couponCode={couponCode}
        refCode={refCode}
        quotes={quotes}
        activeQuote={activeQuote}
        monthlySubsequentPayment={monthlySubsequentPayment}
        monthlyPlanTotal={monthlyPlanTotal}
        loading={loading}
        loadingSubscription={loadingSubscription}
        handleChange={handleChange}
        refreshQuote={refreshQuote}
        refreshQuoteWithoutCoupon={refreshQuoteWithoutCoupon}
      />
      <div className="trust-strip" id="trustStrip">
        <div className="item">
          <svg aria-hidden="true">
            <use href="#icon-shield"></use>
          </svg>
          <span id="trustGuarantee">
            <strong>30-day money-back</strong> guarantee
          </span>
        </div>
        <div className="item">
          <svg aria-hidden="true">
            <use href="#icon-lock"></use>
          </svg>
          <span>
            <strong>SSL encrypted</strong> secure payment
          </span>
        </div>
        <div className="item">
          <svg aria-hidden="true">
            <use href="#icon-star"></use>
          </svg>
          <span>
            <strong>4.9 average rating</strong> from {learnerCountDisplay()} learners
          </span>
        </div>
      </div>
    </aside>
  );
};

export default OrderSummary;
